import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { PowerOff } from 'lucide-react'
import { Card, CardContent } from './ui/card'
import { Button } from './ui/button'
import { modulesAPI } from '../lib/api'
import { useAuth } from '../hooks/useAuth'

/**
 * Wraps a route element. Redirects (via friendly error page) if the
 * given module is disabled and the user is NOT admin/superadmin.
 *
 * Enabled-list is cached in sessionStorage for the tab so we don't
 * hit the API on every route change.
 */

const CACHE_KEY = 'lanserver-enabled-modules-cache'
const CACHE_TTL_MS = 60_000

async function fetchEnabled() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (raw) {
      const { at, keys } = JSON.parse(raw)
      if (Date.now() - at < CACHE_TTL_MS) return keys
    }
  } catch {}
  const { data } = await modulesAPI.getEnabled()
  const keys = data.map(m => m.module_key)
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), keys })) } catch {}
  return keys
}

export function invalidateModuleGuardCache() {
  try { sessionStorage.removeItem(CACHE_KEY) } catch {}
}

export default function ModuleGuard({ moduleKey, children }) {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const [status, setStatus] = useState('loading') // loading | enabled | disabled

  useEffect(() => {
    if (isAdmin) { setStatus('enabled'); return }
    let alive = true
    fetchEnabled()
      .then(keys => { if (alive) setStatus(keys.includes(moduleKey) ? 'enabled' : 'disabled') })
      .catch(() => { if (alive) setStatus('enabled') }) // fail open
    return () => { alive = false }
  }, [moduleKey, isAdmin])

  if (status === 'loading') return null
  if (status === 'disabled') {
    return (
      <div className="container mx-auto p-8 max-w-lg">
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <PowerOff className="h-12 w-12 text-muted-foreground mx-auto" />
            <div>
              <h1 className="text-lg font-bold">Module uitgeschakeld</h1>
              <p className="text-sm text-muted-foreground mt-1">
                De module <code className="px-1.5 py-0.5 rounded bg-muted">{moduleKey}</code> is momenteel niet beschikbaar.
                Vraag een admin om deze in te schakelen.
              </p>
            </div>
            <Button asChild><Link to="/">← Terug naar dashboard</Link></Button>
          </CardContent>
        </Card>
      </div>
    )
  }
  return children
}
